import React, { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { components as griddle } from "griddle-react";

import { Table, TableProps } from "@ui/Table";
import { get, reduce } from "lodash";
import { FormattedMessage, MessageDescriptor } from "react-intl";
import { CellColspanProps, CellEmpty, CellPropsWithRow, enhancedWithRowData } from "../Cells";
import { WaIcon } from "../../WaIcon";

type GetIdFn<T> = (entity: T) => string;
type GetSubRowsFn<T, S> = (entity: T) => Array<Row<T, S>>;
export interface ExpandableProps<T, S> extends TableProps<T> {
    getId: GetIdFn<T>;
    getSubRows?: GetSubRowsFn<T, S>;
    getColspan?: (row: Row<T, S>, columnId: string) => number;
    // limit: number;
    displaySubHeading: boolean;
    className?: string;
    children?: ReactNode;
}

interface Row<T, S> {
    entity: T;
    isSubHeading?: boolean;
    subEntity?: S;
    id: string;
}

interface ExpandableState<T, S> {
    expanded: number;
    rows: Array<Row<T, S>>;
}

interface ExpandableCellOptions {
    mainMap: {[key: string]: string};
    subHeadings?: {[key: string]: string | MessageDescriptor};
    subMap: {[key: string]: string};
    components?: {[key: string]: React.ComponentType<griddle.CellProps>};
    subComponents?: {[key: string]: React.ComponentType<griddle.CellProps>};
}

type CellExpandButtonValue = "expanded" | "collapsed" | "nobutton";

export const CellExpandButton = ({ value, onClick }: griddle.CellProps) => {
    const onClickFn = (e: React.MouseEvent) => {
        e.preventDefault();
        if (onClick != null) {
            onClick(e);
        }
    };
    return (
        <span className="cell-expand-button">
            {value === "expanded" && <a href="#" onClick={onClickFn}>
                <WaIcon name="minus-outline-dark" />
            </a>}
            {value === "collapsed" && <a href="#" onClick={onClickFn}>
                <WaIcon name="add-outline-dark" />
            </a>}
        </span>
    );
};

export const makeExpandableCellComponent = ({
    mainMap,
    subHeadings,
    subMap,
    components,
    subComponents,
}: ExpandableCellOptions) =>
    enhancedWithRowData((props: CellPropsWithRow) => {
        const { columnId, rowData } = props;
        let value = rowData.entity;
        let label = mainMap[columnId];
        let Comp = components != null ? components[columnId] : null;
        // Handle headings
        if (rowData.isSubHeading) {
            const heading = subHeadings != null ? subHeadings[columnId] : "";
            if (heading == null) {
                return <CellEmpty />;
            }
            if (typeof heading === "string") {
                return <>{heading}</>;
            }
            return (
                <FormattedMessage {...heading} />
            );
        }
        else if (rowData.subEntity != null) {
            value = rowData.subEntity;
            label = subMap[columnId];
            Comp = subComponents != null ? subComponents[columnId] : null;
        }
        props = {
            ...props,
            rowData: value,
        };
        const actualValue = get(value, label);
        // value = getValueAsObject(value);
        return <>
            {Comp != null && <Comp {...props} value={actualValue} columnId={label} />}
            {Comp == null && actualValue}
        </>;
    });

const getRows = <T, S, >(
    { data, getId, getSubRows, displaySubHeading }: ExpandableProps<T, S>,
    expanded: number,
): Array<Row<T, S>> =>
    reduce(data, (result: Array<Row<T, S>>, entity: T, key: number = -1) => {
        result.push({ entity, id: getId(entity) });
        if (expanded === key) {
            result = result.concat(
                // The sub-headings row
                ...(displaySubHeading ? [{ entity, id: null, isSubHeading: true }] : []),
                // The sub-rows
                ...getSubRows(entity),
            );
        }
        return result;
    }, []);

type TypedComponent = <T, S>(props: ExpandableProps<T, S>) => React.ReactElement<ExpandableProps<T, S>>;
export const Expandable: TypedComponent = <T, S>(props: ExpandableProps<T, S>) => {
    // Manage the state
    const [state, setState] = useState<ExpandableState<T, S>>({
        expanded: -1,
        rows: getRows(props, -1),
    });
    const stateRef = useRef<ExpandableState<T, S>>(state);
    const propsRef = useRef(props);
    // TODO Handle opened items being removed
    const updateState = (newState: ExpandableState<T, S>) => {
        stateRef.current = newState;
        setState(newState);
    };
    useEffect(() => {
        const { expanded } = stateRef.current;
        propsRef.current = props;
        updateState({
            expanded,
            rows: getRows(props, expanded),
        });
    }, [props]);

    const onClickRow = useCallback((idx: number) => {
        // Close open rows
        let newExpanded = stateRef.current.expanded;
        const { rows } = stateRef.current;
        if (idx === newExpanded) {
            newExpanded = -1;
        }
        // Otherwise open it
        else if (idx > -1) {
            // If the row is after the current expanded row, we need to factor in open rows
            if (idx > newExpanded) {
                newExpanded = idx - rows.filter((row) => row.isSubHeading === true || row.subEntity != null).length;
            }
            else {
                newExpanded = idx;
            }
        }
        updateState({
            expanded: newExpanded,
            rows: getRows(propsRef.current, newExpanded),
        });
    }, [props, state.expanded]);

    // Get the table props
    const {
        getId,
        getSubRows,
        getColspan: _getColspan,
        className,
        children,
        // Need to remove these so tableProps is typed correctly
        data: _data,
        ref: _ref,
        ...tableProps
    } = props;
    // The row enhancer sets the styles and handles the click
    tableProps.RowEnhancer = useCallback(((Comp: React.ComponentType<griddle.RowProps>) =>
        (rowProps: griddle.RowProps) => {
            const { className: rowClassName, griddleKey } = rowProps;
            const { expanded, rows } = stateRef.current;
            const idx = Number(griddleKey);
            const row = rows.length > idx ? rows[idx] : null;
            return (
                <Comp {...rowProps}
                    className={classNames("table-expandable__row", rowClassName, {
                        "table-expandable__row--open": idx === expanded,
                        "table-expandable__row--heading": row != null && row.isSubHeading === true,
                        "table-expandable__row--expanded": row != null && row.subEntity != null,
                    })}
                    // onClick={() => onClickRow(idx)}
                />
            );
        }
    ), [state]);
    tableProps.CellEnhancer = useCallback((Comp: React.ComponentType<CellColspanProps>) =>
        (cellProps: CellColspanProps) => {
            const { griddleKey, columnId } = cellProps;
            const { rows, expanded } = stateRef.current;
            const { getColspan } = propsRef.current;
            const idx = Number(griddleKey);
            const row = rows.length > idx ? rows[idx] : null;
            const colspan = getColspan != null && row != null ? getColspan(row, columnId) : 1;
            // If this is a controls column, we need to set the value
            let value = cellProps.value;
            let onClick = cellProps.onClick;
            if (columnId === "controls" && row != null) {
                if (row.isSubHeading || row.subEntity != null) {
                    value = "nobutton";
                }
                else if (idx === expanded) {
                    value = "expanded";
                }
                else {
                    value = "collapsed";
                }
                value = React.cloneElement(cellProps.value, { value });
                onClick = () => onClickRow(idx);
            }
            return colspan > 0
                ? <Comp {...cellProps} value={value} onClick={onClick} colSpan={colspan} />
                : <></>;
        }, [state, props],
    );
    // Set the size of the table
    if (tableProps.pageSize != null) {
        tableProps.pageSize = reduce(state.rows, (result: number, row: Row<T, S>) => {
            return result + (row.subEntity != null || row.isSubHeading === true ? 1 : 0);
        }, tableProps.pageSize);
    }

    return (
        <div className={classNames("table-expandable", className)}>
            <Table {...tableProps} data={state.rows} >
                {children}
            </Table>
        </div>
    );
};
